import { Search, ShieldCheck, Layers, Award, MapPin } from "lucide-react";

const STEPS = [
  {
    icon: Layers,
    code: "01",
    title: "Acquisizione fonti",
    desc: "Rogiti, atti notarili, banche dati OMI e segnali di mercato sulle aree presidiate vengono raccolti in continuo.",
  },
  {
    icon: Search,
    code: "02",
    title: "Normalizzazione dati",
    desc: "Pulizia, deduplica e applicazione dei coefficienti di omogeneizzazione: stato, piano, vetustà, esposizione.",
  },
  {
    icon: ShieldCheck,
    code: "03",
    title: "Comparazione MCM",
    desc: "Ogni immobile target viene confrontato con i comparabili statisticamente coerenti, secondo lo standard IVS.",
  },
  {
    icon: Award,
    code: "04",
    title: "Output difendibile",
    desc: "Range di valore con intervallo di confidenza, log di derivazione e dossier difendibile in sede peritale.",
  },
];

export default function LogicaCertezza() {
  return (
    <section id="radar" className="relative py-24 lg:py-32 bg-white border-t border-slate-100 overflow-hidden" data-testid="logica-section">
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-y-10 gap-x-2 md:gap-10 lg:gap-14 items-start">
          <div className="col-span-12 lg:col-span-5">
            <div className="enea-eyebrow flex items-center gap-3 mb-5">
              <ShieldCheck className="w-4 h-4 text-[#2c7da0]" />
              <span>04 · Logica di Certezza</span>
            </div>
            <h2 className="text-3xl lg:text-4xl xl:text-[44px] font-display font-semibold leading-tight text-[#0b2545]">
              Il mercato si muove.
              <span className="block mt-3 text-[#2c7da0] font-normal">Noi lo seguiamo, ogni giorno.</span>
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Un presidio continuo del territorio attraverso fonti certificate e rilevazione diretta.
              Ogni nuova transazione, ogni variazione normativa, ogni offerta significativa entra nel
              modello e ne ricalibra il giudizio estimativo.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Il valore tecnico che Home Exchange Copilot AI consegna non è una fotografia statica:
              è un giudizio allineato al mercato del momento, documentato e replicabile.
            </p>

            <div className="mt-8 rounded-xl bg-slate-50 border border-slate-200 p-6 space-y-4" data-testid="radar-status-panel">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-[0.18em] font-medium text-slate-500">Presidio territorio</span>
                <span className="inline-flex items-center gap-2 text-sm text-[#0d9488] font-medium">
                  <span className="w-2 h-2 rounded-full bg-[#0d9488]" />
                  Attivo
                </span>
              </div>
              <div className="flex items-center justify-between border-t border-slate-200 pt-3">
                <span className="text-sm text-slate-600">Aree monitorate</span>
                <span className="text-sm font-semibold text-[#0b2545] inline-flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-[#2c7da0]" />
                  Tutta Italia
                </span>
              </div>
              <div className="flex items-center justify-between border-t border-slate-200 pt-3">
                <span className="text-sm text-slate-600">Comparabili attivi</span>
                <span className="text-sm font-semibold text-[#0b2545]">1.482</span>
              </div>
              <div className="flex items-center justify-between border-t border-slate-200 pt-3">
                <span className="text-sm text-slate-600">Calibrazione modello</span>
                <span className="text-sm font-semibold text-[#0b2545]">Continua</span>
              </div>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-7">
            <div className="rounded-2xl overflow-hidden shadow-xl shadow-slate-200/50 border border-slate-200 mb-6">
              <img
                src="https://images.unsplash.com/photo-1582407947304-fd86f028f716?auto=format&fit=crop&w=1600&q=80"
                alt="Architettura italiana"
                className="w-full h-64 lg:h-80 object-cover"
                onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=1600&q=80"; }}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {STEPS.map((s) => {
                const Icon = s.icon;
                return (
                  <div
                    key={s.code}
                    className="group rounded-xl bg-white border border-slate-200 p-7 shadow-sm hover:shadow-md hover:border-[#2c7da0]/40 transition-all duration-200"
                    data-testid={`radar-step-${s.code}`}
                  >
                    <div className="flex items-start justify-between mb-5">
                      <div className="w-12 h-12 rounded-lg bg-[#0b2545]/5 group-hover:bg-[#0b2545] flex items-center justify-center transition-colors">
                        <Icon className="w-5 h-5 text-[#0b2545] group-hover:text-white transition-colors" />
                      </div>
                      <span className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#2c7da0]">{s.code}</span>
                    </div>
                    <h3 className="text-lg font-display font-semibold tracking-tight text-[#0b2545]">{s.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
